import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Field,
  Icon,
  IconBtn,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
} from "../../../design-system";
import { requestWorkdayCorrection } from "../../../helpers/kronaly_helper";

function pad(n) { return String(n).padStart(2, "0"); }

function isoToHHMM(iso, tz) {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat("en-GB", {
      timeZone: tz,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(new Date(iso));
  } catch {
    return "";
  }
}

function tzOffsetForDate(dateYmd, tz) {
  try {
    const probe = new Date(`${dateYmd}T12:00:00Z`);
    const parts = new Intl.DateTimeFormat("en", {
      timeZone: tz,
      timeZoneName: "longOffset",
    }).formatToParts(probe);
    const raw = parts.find((p) => p.type === "timeZoneName")?.value || "GMT+00:00";
    const off = raw.replace("GMT", "");
    return off === "" ? "+00:00" : off;
  } catch {
    return "+00:00";
  }
}

function hhmmToIsoInTz(dateYmd, hhmm, tz) {
  if (!hhmm) return null;
  const [hRaw, mRaw] = hhmm.split(":");
  if (hRaw == null || mRaw == null) return null;
  const offset = tzOffsetForDate(dateYmd, tz);
  return `${dateYmd}T${pad(hRaw)}:${pad(mRaw)}:00${offset}`;
}

function minutesFromHHMM(hhmm) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map((n) => parseInt(n, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function computeTotalMinutes(segments, breaks) {
  let total = 0;
  for (const s of segments) {
    const inM = minutesFromHHMM(s.in);
    const outM = minutesFromHHMM(s.out);
    if (inM != null && outM != null && outM > inM) {
      total += outM - inM;
    }
  }
  for (const b of breaks) {
    const sM = minutesFromHHMM(b.start);
    const eM = minutesFromHHMM(b.end);
    if (sM != null && eM != null && eM > sM) {
      total -= eM - sM;
    }
  }
  return Math.max(0, total);
}

function fmtDur(min) {
  if (min == null) return "—";
  return `${pad(Math.floor(min / 60))}:${pad(min % 60)}`;
}

function unwrapApiError(e) {
  return (
    e?.response?.data?.message
    || e?.response?.data?.error
    || (typeof e === "string" ? e : e?.message)
    || "No se pudo aplicar la corrección."
  );
}

export function CorregirJornadaModal({
  open,
  day,
  tz,
  onClose,
  onApplied,
  correctionMode = "requires_approval",
  targetEmployeeProfileId = null,
  isOnBehalf = false,
}) {
  // on_behalf siempre exige aprobación del empleado afectado.
  const effectiveMode = isOnBehalf ? "requires_approval" : correctionMode;
  const date = day?.date;
  const [segments, setSegments] = useState([]);
  const [breaks, setBreaks] = useState([]);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !day) return;
    const initSegs = (day.record?.segments || []).map((s) => ({
      in: isoToHHMM(s.clock_in_at, tz),
      out: isoToHHMM(s.clock_out_at, tz),
    }));
    const initBreaks = (day.record?.breaks || []).map((b) => ({
      start: isoToHHMM(b.start_at, tz),
      end: isoToHHMM(b.end_at, tz),
    }));
    setSegments(initSegs.length > 0 ? initSegs : [{ in: "", out: "" }]);
    setBreaks(initBreaks);
    setNote("");
    setError(null);
    setSubmitting(false);
  }, [open, day, tz]);

  const totalMin = useMemo(() => computeTotalMinutes(segments, breaks), [segments, breaks]);
  const plannedMin = day?.planned_minutes || 0;
  const diff = totalMin - plannedMin;

  const updateSegment = (idx, field, value) => {
    setSegments((prev) => prev.map((s, i) => (i === idx ? { ...s, [field]: value } : s)));
  };
  const addSegment = () => setSegments((prev) => [...prev, { in: "", out: "" }]);
  const removeSegment = (idx) => setSegments((prev) => prev.filter((_, i) => i !== idx));

  const updateBreak = (idx, field, value) => {
    setBreaks((prev) => prev.map((b, i) => (i === idx ? { ...b, [field]: value } : b)));
  };
  const addBreak = () => setBreaks((prev) => [...prev, { start: "", end: "" }]);
  const removeBreak = (idx) => setBreaks((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async () => {
    setError(null);

    const cleanSegs = segments.filter((s) => s.in);
    if (cleanSegs.length === 0) {
      setError("Debes indicar al menos un segmento con hora de entrada.");
      return;
    }
    for (const s of cleanSegs) {
      const inM = minutesFromHHMM(s.in);
      const outM = minutesFromHHMM(s.out);
      if (outM != null && inM != null && outM <= inM) {
        setError("La hora de salida debe ser posterior a la de entrada.");
        return;
      }
    }
    const cleanBreaks = breaks.filter((b) => b.start || b.end);
    for (const b of cleanBreaks) {
      const sM = minutesFromHHMM(b.start);
      const eM = minutesFromHHMM(b.end);
      if (sM == null || eM == null) {
        setError("Cada pausa debe tener hora de inicio y fin.");
        return;
      }
      if (eM <= sM) {
        setError("El fin de la pausa debe ser posterior al inicio.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = {
        segments: cleanSegs.map((s) => ({
          clock_in_at: hhmmToIsoInTz(date, s.in, tz),
          clock_out_at: s.out ? hhmmToIsoInTz(date, s.out, tz) : null,
        })),
        breaks: cleanBreaks.map((b) => ({
          start_at: hhmmToIsoInTz(date, b.start, tz),
          end_at: hhmmToIsoInTz(date, b.end, tz),
        })),
        note: note.trim() || null,
      };
      if (isOnBehalf && targetEmployeeProfileId) {
        payload.employee_profile_id = targetEmployeeProfileId;
      }
      await requestWorkdayCorrection(date, payload);
      onApplied?.();
      onClose?.();
    } catch (e) {
      setError(unwrapApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const title = (
    <div>
      <div style={{ fontSize: 11, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--kr-fg-muted)", fontWeight: 600 }}>
        Corregir jornada
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--kr-fg-strong)", marginTop: 2 }}>
        {date}
      </div>
    </div>
  );

  return (
    <Modal open={open} onClose={onClose} title={title} size="lg">
      <ModalBody>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {error && <Alert tone="danger">{error}</Alert>}

          <div style={{
            padding: 12, borderRadius: 6, background: "var(--kr-status-info-bg)",
            display: "flex", gap: 10, alignItems: "flex-start",
          }}>
            <Icon name="Info" size={16} style={{ color: "var(--kr-info-700)", flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 12, color: "var(--kr-fg-strong)", lineHeight: 1.5 }}>
              {isOnBehalf ? (
                <>Estás corrigiendo la jornada de otro empleado. La solicitud quedará <strong>pendiente
                hasta que el empleado afectado la apruebe</strong>. Quedará registrada en el histórico
                de auditoría junto con el estado previo, la fecha y el usuario que la realiza.</>
              ) : effectiveMode === "requires_approval" ? (
                <>Esta corrección quedará <strong>pendiente de aprobación</strong> de un manager u owner.
                Quedará registrada en el histórico de auditoría junto con el estado previo,
                la fecha y el usuario que la realiza, en cumplimiento de la normativa de fichaje digital.</>
              ) : (
                <>Esta corrección se aplicará inmediatamente y quedará registrada en el histórico de auditoría
                junto con el estado previo, la fecha y el usuario que la realiza, en cumplimiento de la normativa
                de fichaje digital.</>
              )}
            </div>
          </div>

          <section>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)" }}>
                Entradas y salidas
              </div>
              <Button variant="ghost" size="sm" icon="Plus" onClick={addSegment}>
                Añadir segmento
              </Button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {segments.map((s, idx) => (
                <div key={idx} style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr auto",
                  gap: 10,
                  alignItems: "end",
                  padding: 10,
                  border: "1px solid var(--kr-border-subtle)",
                  borderRadius: 6,
                }}>
                  <Field label={`Entrada ${segments.length > 1 ? `#${idx + 1}` : ""}`}>
                    <Input
                      type="time"
                      value={s.in}
                      onChange={(e) => updateSegment(idx, "in", e.target.value)}
                    />
                  </Field>
                  <Field label="Salida">
                    <Input
                      type="time"
                      value={s.out}
                      onChange={(e) => updateSegment(idx, "out", e.target.value)}
                    />
                  </Field>
                  <IconBtn
                    icon="Trash2"
                    title="Eliminar segmento"
                    onClick={() => removeSegment(idx)}
                    disabled={segments.length <= 1}
                  />
                </div>
              ))}
            </div>
          </section>

          <section>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "var(--kr-fg-strong)" }}>
                Pausas
              </div>
              <Button variant="ghost" size="sm" icon="Plus" onClick={addBreak}>
                Añadir pausa
              </Button>
            </div>
            {breaks.length === 0 ? (
              <div style={{
                padding: 12,
                border: "1px dashed var(--kr-border-subtle)",
                borderRadius: 6,
                fontSize: 12,
                color: "var(--kr-fg-muted)",
                textAlign: "center",
              }}>
                Sin pausas registradas.
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {breaks.map((b, idx) => (
                  <div key={idx} style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr auto",
                    gap: 10,
                    alignItems: "end",
                    padding: 10,
                    border: "1px solid var(--kr-border-subtle)",
                    borderRadius: 6,
                  }}>
                    <Field label={`Inicio pausa${breaks.length > 1 ? ` #${idx + 1}` : ""}`}>
                      <Input
                        type="time"
                        value={b.start}
                        onChange={(e) => updateBreak(idx, "start", e.target.value)}
                      />
                    </Field>
                    <Field label="Fin pausa">
                      <Input
                        type="time"
                        value={b.end}
                        onChange={(e) => updateBreak(idx, "end", e.target.value)}
                      />
                    </Field>
                    <IconBtn icon="Trash2" title="Eliminar pausa" onClick={() => removeBreak(idx)} />
                  </div>
                ))}
              </div>
            )}
          </section>

          <Field
            label="Motivo o nota (opcional)"
            help="Indica brevemente por qué necesitas corregir esta jornada."
          >
            <Input
              type="textarea"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              maxLength={2000}
              placeholder="Olvidé marcar la entrada al llegar…"
            />
          </Field>
        </div>
      </ModalBody>
      <ModalFooter style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", gap: 18, alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600 }}>
              Total propuesto
            </div>
            <div style={{ fontFamily: "var(--kr-font-mono)", fontSize: 18, fontWeight: 700, color: "var(--kr-fg-strong)" }}>
              {fmtDur(totalMin)} h
            </div>
          </div>
          {plannedMin > 0 && (
            <div>
              <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--kr-fg-muted)", fontWeight: 600 }}>
                Diferencia
              </div>
              <div style={{
                fontFamily: "var(--kr-font-mono)",
                fontSize: 14,
                fontWeight: 700,
                color: diff >= 0 ? "var(--kr-success-700)" : "var(--kr-warning-700)",
              }}>
                {diff >= 0 ? "+" : "−"}{fmtDur(Math.abs(diff))} h
              </div>
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={submitting} icon={submitting ? "Loader2" : "Check"}>
            {submitting
              ? (effectiveMode === "requires_approval" ? "Enviando…" : "Aplicando…")
              : (effectiveMode === "requires_approval" ? "Solicitar corrección" : "Aplicar corrección")}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
}

export default CorregirJornadaModal;
